
import React from "react";
import { AIStaffMember, AIStaffCandidate } from "@/lib/types";

// Import stage components
import AvailableAssistantsStage from "./AvailableAssistantsStage";
import InterviewStage from "./InterviewStage";
import OnboardingStage from "./OnboardingStage";
import ManagementStage from "./stages/ManagementStage";
import { useStaff } from "@/hooks/staff/useAllStaff";

interface StageRendererProps {
  currentStage: number;
  selectedCandidate: AIStaffCandidate | null;
  isSubmitting: boolean;
  aiStaff: AIStaffMember | null;
  staffId?: string;
  isLoading?: boolean;
  onSelectCandidate: (candidate: AIStaffCandidate) => void;
  onSelectPlan: (plan: string) => void;
  onComplete: () => void;
  onCancelSubscription: () => void;
}

// Update AvailableAssistantsStage prop types to accept string | number for ID
type CandidateDisplay = {
  id: number | string;
  name: string;
  avatar: string;
  role: string;
  description: string;
  skills: string[];
  benefits: string;
};

const StageRenderer: React.FC<StageRendererProps> = ({
  currentStage,
  selectedCandidate,
  isSubmitting,
  aiStaff,
  staffId,
  isLoading = false,
  onSelectCandidate,
  onSelectPlan,
  onComplete,
  onCancelSubscription
}) => {
  // Get AI candidates data from the hook
  const { data: aiCandidates = [] } = useStaff();
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-pulse h-6 w-6 rounded-full bg-primary/20"></div>
      </div>
    );
  }
  
  // Render the appropriate stage content
  switch (currentStage) {
    case 1:
      // Convert candidates to the format expected by AvailableAssistantsStage
      const displayCandidates: CandidateDisplay[] = aiCandidates.map(candidate => ({
        id: Number(candidate.id), // Convert to number for display
        name: candidate.name,
        avatar: candidate.avatar || "👩‍💼",
        role: candidate.role,
        description: candidate.description || "",
        skills: candidate.abilities || [],
        benefits: "Boost your marketing effectiveness with AI-powered content creation and strategy."
      }));
      
      return (
        <AvailableAssistantsStage
          aiCandidates={displayCandidates}
          onSelectCandidate={(id) => {
            const candidate = aiCandidates.find(c => Number(c.id) === id);
            if (candidate) {
              onSelectCandidate(candidate);
            }
          }}
          selectedCandidate={selectedCandidate ? Number(selectedCandidate.id) : null}
        />
      );
    
    case 2:
      return (
        <InterviewStage 
          candidate={selectedCandidate ? {
            id: Number(selectedCandidate.id), // Convert to number for display
            name: selectedCandidate.name,
            avatar: selectedCandidate.avatar || "👩‍💼",
            role: selectedCandidate.role,
            description: selectedCandidate.description || "",
            skills: selectedCandidate.abilities || [],
            benefits: "Boost your marketing effectiveness with AI-powered content creation and strategy."
          } : undefined}
          roleCapabilities={{
            marketing: [
              "Generate social media content for all major platforms",
              "Create email marketing campaigns",
              "Perform SEO and keyword research",
              "Optimize ad copy and content",
              "Analyze performance metrics and provide recommendations"
            ]
          }}
          onSelectPlan={onSelectPlan}
        />
      );
    
    case 3:
      return (
        <OnboardingStage 
          aiStaff={aiStaff}
          onComplete={onComplete}
        />
      );
    
    case 4:
      return (
        <ManagementStage 
          aiStaff={aiStaff} 
          staffId={staffId} 
          isSubmitting={isSubmitting}
          onCancelSubscription={onCancelSubscription}
        />
      );
    
    default:
      return <div>Unknown stage</div>;
  }
};

export default StageRenderer;
